import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  Camera, FileText, Users, TrendingUp, 
  CheckCircle2, ArrowRight, Phone 
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ContactForm from "@/components/ContactForm";
import { Button } from "@/components/ui/button";
import SEO, { SEO_DATA } from "@/components/SEO";

const SellPage = () => {
  const { t } = useTranslation();
  
  const steps = [
    {
      number: "01",
      title: t('sell.steps.evaluation.title'),
      description: t('sell.steps.evaluation.description')
    },
    {
      number: "02",
      title: t('sell.steps.preparation.title'),
      description: t('sell.steps.preparation.description')
    },
    {
      number: "03",
      title: t('sell.steps.marketing.title'),
      description: t('sell.steps.marketing.description')
    },
    {
      number: "04",
      title: t('sell.steps.negotiation.title'),
      description: t('sell.steps.negotiation.description')
    },
    {
      number: "05",
      title: t('sell.steps.closing.title'),
      description: t('sell.steps.closing.description')
    }
  ];

  const benefits = [
    {
      icon: Camera,
      title: t('sell.benefits.photography.title'),
      description: t('sell.benefits.photography.description')
    },
    {
      icon: FileText,
      title: t('sell.benefits.documentation.title'),
      description: t('sell.benefits.documentation.description')
    },
    {
      icon: Users,
      title: t('sell.benefits.network.title'),
      description: t('sell.benefits.network.description')
    },
    {
      icon: TrendingUp,
      title: t('sell.benefits.pricing.title'),
      description: t('sell.benefits.pricing.description')
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="sell-page">
      <SEO 
        title={SEO_DATA.sell.title}
        description={SEO_DATA.sell.description}
        keywords={SEO_DATA.sell.keywords}
        url="/vender"
      />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="text-[#D4AF37] text-sm uppercase tracking-widest">
                {t('sell.label')}
              </span>
              <h1 className="mt-4 text-4xl md:text-5xl text-white leading-tight">
                {t('sell.heroTitle')}
                <span className="text-[#D4AF37]"> {t('sell.heroHighlight')}</span>
              </h1>
              <p className="mt-6 text-lg text-[#A3A3A3] leading-relaxed">
                {t('sell.heroDescription')}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a href="#contact-form">
                  <Button 
                    className="bg-[#D4AF37] text-black hover:bg-[#FFE082] px-8 py-6 uppercase tracking-wider"
                    data-testid="sell-cta-btn"
                  >
                    Solicitar Evaluación
                  </Button>
                </a>
                <a 
                  href={`https://wa.me/50242547204?text=${encodeURIComponent("¡Hola Ikonic Deals! 👋\n\nEstoy interesado/a en vender o alquilar mi propiedad.\n\n¿Podrían darme más información sobre su servicio?")}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button 
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5 px-8 py-6 uppercase tracking-wider"
                    data-testid="sell-whatsapp-btn"
                  >
                    <Phone size={18} className="mr-2" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-2 gap-6"
            >
              {[
                { number: "95%", label: "Tasa de Éxito" },
                { number: "90", label: "Días Promedio" },
                { number: "350+", label: "Propiedades Vendidas" },
                { number: "$50M+", label: "En Transacciones" }
              ].map((stat, index) => (
                <div 
                  key={stat.label}
                  className="p-6 bg-[#121212] border border-white/5 text-center"
                >
                  <p className="text-3xl md:text-4xl text-[#D4AF37] font-serif">{stat.number}</p>
                  <p className="mt-2 text-sm text-[#A3A3A3] uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-[#0a0a0a]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest">
              Nuestros Servicios
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl text-white">
              ¿Qué incluye nuestro servicio?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-[#121212] border border-white/5 hover:border-[#D4AF37]/30 transition-all duration-500 text-center"
              >
                <div className="w-14 h-14 mx-auto flex items-center justify-center bg-[#D4AF37]/10 mb-4">
                  <benefit.icon size={26} className="text-[#D4AF37]" />
                </div>
                <h3 className="text-lg text-white mb-2">{benefit.title}</h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest">
              Proceso Simple
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl text-white">
              ¿Cómo funciona?
            </h2>
          </motion.div>

          <div className="space-y-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-6 md:gap-8 items-start p-6 md:p-8 bg-[#121212]/50 border border-white/5 hover:border-[#D4AF37]/20 transition-all"
              >
                <span className="text-4xl md:text-5xl font-serif text-[#D4AF37]/30">
                  {step.number}
                </span>
                <div>
                  <h3 className="text-xl text-white mb-2">{step.title}</h3>
                  <p className="text-[#A3A3A3] leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Sell With Us */}
      <section className="py-16 md:py-24 bg-[#0a0a0a]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#D4AF37] text-sm uppercase tracking-widest">
                {t('sell.whyUs.label')}
              </span>
              <h2 className="mt-4 text-3xl md:text-4xl text-white">
                {t('sell.whyUs.title')}
              </h2>
              <ul className="mt-8 space-y-4">
                {[
                  t('sell.whyUs.advantage1'),
                  t('sell.whyUs.advantage2'),
                  t('sell.whyUs.advantage3'),
                  t('sell.whyUs.advantage4'),
                  t('sell.whyUs.advantage5'),
                  t('sell.whyUs.advantage6')
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-[#A3A3A3]">
                    <CheckCircle2 size={20} className="text-[#D4AF37] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              id="contact-form"
              className="bg-[#121212] border border-white/5 p-8 md:p-10"
            >
              <h3 className="text-2xl text-white mb-2">{t('sell.form.title')}</h3>
              <p className="text-[#A3A3A3] mb-8">
                {t('sell.form.subtitle')}
              </p>
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl text-white mb-6">
              {t('sell.cta.title')}
            </h2>
            <p className="text-[#A3A3A3] mb-8">
              {t('sell.cta.subtitle')}
            </p>
            <Link to="/propiedades">
              <Button 
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-8 py-6 uppercase tracking-wider"
              >
                {t('sell.cta.viewProperties')}
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SellPage;
